import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { courseList } from "../../api/Courses";
import { CreateSyllabus, FetchSyllabus } from "../../api/SyllabusService";
import { ToastMain, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { useNavigate } from "react-router-dom";
import AcademicHeader from "../Academic/AcademicHeader";

// NOTE: this component uses the SheetJS library to read .xlsx/.csv files
// in the browser. If it isn't already in the project, install it with:
//   npm install xlsx

export default function SyllabusManagement() {
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [selectedSession, setSelectedSession] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");

  const [existingTopics, setExistingTopics] = useState([]);

  const [newTopics, setNewTopics] = useState([
    {
      title: "",
      description: "",
      duration_day: "",
    },
  ]);

  // --------------------------------------------------
  // Add-mode: manual entry vs Excel import
  // --------------------------------------------------
  const [addMode, setAddMode] = useState("manual"); // "manual" | "import"

  const [importFileName, setImportFileName] = useState("");
  const [importRows, setImportRows] = useState([]); // { rowId, title, description, duration_day, status, error }
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const storedSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");

    setSessions(storedSessions);

    const activeSession = storedSessions.find(
      (session) => String(session.status) === "1",
    );

    if (activeSession) {
      setSelectedSession(String(activeSession.id));
    }
  }, []);

  const courseFetch = async () => {
    setLoading(true);

    try {
      const res = await courseList();

      if (res?.data?.status) {
        setCourses(res?.data?.data || []);
      } else {
        setCourses([]);
        SweetAlertShow("info", "Warning", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    courseFetch();
  }, []);

  // --------------------------------------------------
  // Course Change
  // Course already contains subjects_list
  // --------------------------------------------------
  const handleCourseChange = (courseId) => {
    setSelectedCourse(courseId);
    setSelectedSubject("");
    setExistingTopics([]);

    const selectedCourseData = courses.find(
      (course) => String(course.id) === String(courseId),
    );

    setSubjects(selectedCourseData?.subjects_list || []);
  };

  const handleSessionChange = (sessionId) => {
    setSelectedSession(sessionId);
    setExistingTopics([]);
  };

  const syllabusFetch = async () => {
    if (!selectedSession || !selectedCourse || !selectedSubject) {
      setExistingTopics([]);
      return;
    }

    setLoading(true);

    try {
      const addUrl =
        `&session_id=${selectedSession}` +
        `&course_id=${selectedCourse}` +
        `&subject_id=${selectedSubject}` +
        `&search=${encodeURIComponent(search)}`;

      const res = await FetchSyllabus(addUrl);

      if (res?.data?.status && res?.data?.data?.data?.length > 0) {
        const existing = res.data.data.data.map((item) => ({
          id: item.id,
          title: item.title || "",
          description: item.description || "",
          duration_day: item.duration_day || "",
        }));

        setExistingTopics(existing);
      } else {
        setExistingTopics([]);
      }
    } catch (err) {
      setExistingTopics([]);

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSession && selectedCourse && selectedSubject) {
      syllabusFetch();
    } else {
      setExistingTopics([]);
    }
  }, [selectedSession, selectedCourse, selectedSubject]);

  useEffect(() => {
    if (selectedSession && selectedCourse && selectedSubject) {
      const timer = setTimeout(() => {
        syllabusFetch();
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [search]);

  // reset the import panel whenever the session/course/subject context changes,
  // so a stale preview from a different subject can't get submitted by mistake
  useEffect(() => {
    resetImport();
  }, [selectedSession, selectedCourse, selectedSubject]);

  const handleNewTopicChange = (index, field, value) => {
    const updated = [...newTopics];
    updated[index][field] = value;
    setNewTopics(updated);
  };

  const handleAddNewTopic = () => {
    setNewTopics([
      ...newTopics,
      {
        title: "",
        description: "",
        duration_day: "",
      },
    ]);
  };

  const handleDeleteNewTopic = (index) => {
    if (newTopics.length === 1) return;
    setNewTopics(newTopics.filter((_, idx) => idx !== index));
  };

  const addSyllabus = async (e) => {
    e.preventDefault();

    if (!selectedSession) {
      SweetAlertShow("info", "Warning", "Please select session.");
      return;
    }

    if (!selectedCourse) {
      SweetAlertShow("info", "Warning", "Please select course.");
      return;
    }

    if (!selectedSubject) {
      SweetAlertShow("info", "Warning", "Please select subject.");
      return;
    }

    // Remove completely empty rows
    const topicsToSubmit = newTopics.filter(
      (topic) =>
        topic.title?.trim() || topic.description?.trim() || topic.duration_day,
    );

    if (topicsToSubmit.length === 0) {
      ToastMain.fire({
        icon: "info",
        title: "Please add at least one syllabus topic.",
      });

      return;
    }

    // Validate topics
    const invalidTopic = topicsToSubmit.find(
      (topic) => !topic.title?.trim() || !topic.duration_day,
    );

    if (invalidTopic) {
      SweetAlertShow(
        "info",
        "Warning",
        "Topic title and duration are required.",
      );

      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("session_id", selectedSession);
      formData.append("course_id", selectedCourse);
      formData.append("subject_id", selectedSubject);

      topicsToSubmit.forEach((topic) => {
        formData.append("title[]", topic.title);
        formData.append("duration_day[]", topic.duration_day);
        formData.append("description[]", topic.description || "");
      });

      const response = await CreateSyllabus(formData);

      if (response?.data?.status) {
        ToastMain.fire({
          icon: "success",
          title: response?.data?.message || "Syllabus added successfully.",
        });

        // Reset new topics
        setNewTopics([
          {
            title: "",
            description: "",
            duration_day: "",
          },
        ]);

        // Reload existing topics
        await syllabusFetch();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // Excel import
  // --------------------------------------------------

  // Accepts a header row of Topic / Description / Duration (case-insensitive,
  // a few common aliases supported) and parses it into importRows.
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    setImportRows([]);

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (!rows.length) {
          SweetAlertShow(
            "info",
            "Warning",
            "The selected file doesn't have any data rows.",
          );
          return;
        }

        const normalized = rows.map((row, idx) => {
          const title = row.Topic ?? row.topic ?? row.Title ?? row.title ?? "";
          const description =
            row.Description ?? row.description ?? row.Desc ?? row.desc ?? "";
          const duration =
            row.Duration ??
            row.duration ??
            row["Duration (Days)"] ??
            row.duration_day ??
            "";

          return {
            rowId: `import-${idx}-${Date.now()}`,
            title: String(title).trim(),
            description: String(description).trim(),
            duration_day: String(duration).trim(),
            status: "pending", // pending | uploading | success | failed
            error: "",
          };
        });

        setImportRows(normalized);
      } catch (err) {
        SweetAlertShow(
          "error",
          "Error",
          "Couldn't read that file. Please make sure it's a valid .xlsx, .xls or .csv export.",
        );
      }
    };

    reader.readAsArrayBuffer(file);

    // allow re-selecting the same file name later
    e.target.value = "";
  };

  const downloadImportTemplate = () => {
    const sample = [
      {
        Topic: "Introduction to Algebra",
        Description: "Basics of variables and expressions",
        Duration: 5,
      },
      { Topic: "", Description: "", Duration: "" },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sample);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Syllabus");
    XLSX.writeFile(workbook, "syllabus_import_template.xlsx");
  };

  const resetImport = () => {
    setImportRows([]);
    setImportFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImportRow = (rowId) => {
    setImportRows((prev) => prev.filter((r) => r.rowId !== rowId));
  };

  // Submits ONE topic row against the existing single-topic-array contract
  // the backend already expects, so we can track success/failure per row.
  const submitSingleTopic = async (topic) => {
    const formData = new FormData();

    formData.append("session_id", selectedSession);
    formData.append("course_id", selectedCourse);
    formData.append("subject_id", selectedSubject);
    formData.append("title[]", topic.title);
    formData.append("duration_day[]", topic.duration_day);
    formData.append("description[]", topic.description || "");

    const response = await CreateSyllabus(formData);

    if (response?.data?.status) {
      return { success: true };
    }

    return {
      success: false,
      error: response?.data?.message || "Failed to save this row.",
    };
  };

  const startImport = async () => {
    if (!selectedSession || !selectedCourse || !selectedSubject) {
      SweetAlertShow(
        "info",
        "Warning",
        "Please select session, course and subject first.",
      );
      return;
    }

    // (re)try anything that isn't already saved
    const rowsToProcess = importRows.filter((r) => r.status !== "success");

    if (rowsToProcess.length === 0) {
      ToastMain.fire({ icon: "info", title: "Nothing left to import." });
      return;
    }

    setImporting(true);

    for (const row of rowsToProcess) {
      setImportRows((prev) =>
        prev.map((r) =>
          r.rowId === row.rowId ? { ...r, status: "uploading", error: "" } : r,
        ),
      );

      const durationNum = Number(row.duration_day);

      if (
        !row.title ||
        !row.duration_day ||
        isNaN(durationNum) ||
        durationNum <= 0
      ) {
        setImportRows((prev) =>
          prev.map((r) =>
            r.rowId === row.rowId
              ? {
                  ...r,
                  status: "failed",
                  error: "Missing or invalid topic/duration",
                }
              : r,
          ),
        );
        continue;
      }

      try {
        const result = await submitSingleTopic(row);

        setImportRows((prev) =>
          prev.map((r) =>
            r.rowId === row.rowId
              ? result.success
                ? { ...r, status: "success", error: "" }
                : { ...r, status: "failed", error: result.error }
              : r,
          ),
        );
      } catch (err) {
        setImportRows((prev) =>
          prev.map((r) =>
            r.rowId === row.rowId
              ? {
                  ...r,
                  status: "failed",
                  error:
                    err?.response?.data?.message ??
                    err?.response?.message ??
                    err?.message ??
                    "Failed to save this row.",
                }
              : r,
          ),
        );
      }
    }

    setImporting(false);

    // pull in whatever made it in successfully
    await syllabusFetch();
  };

  const importTotal = importRows.length;
  const importSuccessCount = importRows.filter(
    (r) => r.status === "success",
  ).length;
  const importFailedCount = importRows.filter(
    (r) => r.status === "failed",
  ).length;
  const importRemainingCount =
    importTotal - importSuccessCount - importFailedCount;
  const importDonePct = importTotal
    ? Math.round(((importSuccessCount + importFailedCount) / importTotal) * 100)
    : 0;

  const existingDuration = existingTopics.reduce(
    (total, item) => total + Number(item.duration_day || 0),
    0,
  );

  const newTopicsDuration = newTopics.reduce(
    (total, item) => total + (Number(item.duration_day) || 0),
    0,
  );

  const contextReady = selectedSession && selectedCourse && selectedSubject;

  return (
    <>
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 9999, background: "rgba(255,255,255,0.6)" }}
        >
          <div className="sk-chase sk-primary display-1">
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
          </div>
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row mt-4">
                  <AcademicHeader />

                  <div className="col-md-12">
                    <div className="card shadow-sm border-0">
                      {/* Header */}
                      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center"
                            style={{ width: 44, height: 44 }}
                          >
                            <i className="ri-book-2-line fs-4" />
                          </div>
                          <div>
                            <h4 className="mb-0 text-primary">
                              Syllabus Management
                            </h4>
                            <small className="text-muted">
                              Manage course wise subject syllabus
                            </small>
                          </div>
                        </div>

                        {existingTopics.length > 0 && (
                          <span className="badge bg-label-primary rounded-pill px-3 py-2">
                            {existingTopics.length}{" "}
                            {existingTopics.length === 1 ? "Topic" : "Topics"} ·{" "}
                            {existingDuration} Days
                          </span>
                        )}
                      </div>

                      <div className="card-body">
                        {/* Step 1 */}
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span
                            className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: 24, height: 24, fontSize: 12 }}
                          >
                            1
                          </span>
                          <h6 className="mb-0">Basic Details</h6>
                        </div>

                        <div className="row g-2 mb-4">
                          {/* Session */}
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                name="session_id"
                                value={selectedSession}
                                onChange={(e) =>
                                  handleSessionChange(e.target.value)
                                }
                                required
                              >
                                <option value="">Select Session</option>
                                {sessions?.map((session) => (
                                  <option key={session.id} value={session.id}>
                                    {session.session}
                                    {String(session.status) === "1"
                                      ? " : Active"
                                      : " : Inactive"}
                                  </option>
                                ))}
                              </select>
                              <label>Select Session</label>
                            </div>
                          </div>

                          {/* Course */}
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                name="course_id"
                                value={selectedCourse}
                                onChange={(e) =>
                                  handleCourseChange(e.target.value)
                                }
                                required
                              >
                                <option value="">Select Course</option>
                                {courses?.map((course) => (
                                  <option key={course.id} value={course.id}>
                                    {course.title}
                                  </option>
                                ))}
                              </select>
                              <label>Select Course</label>
                            </div>
                          </div>

                          {/* Subject */}
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                name="subject_id"
                                value={selectedSubject}
                                onChange={(e) =>
                                  setSelectedSubject(e.target.value)
                                }
                                required
                                disabled={!selectedCourse}
                              >
                                <option value="">
                                  {!selectedCourse
                                    ? "Select Course First"
                                    : "Select Subject"}
                                </option>
                                {subjects?.map((subject) => (
                                  <option key={subject.id} value={subject.id}>
                                    {subject.name}
                                  </option>
                                ))}
                              </select>
                              <label>Select Subject</label>
                            </div>
                          </div>
                        </div>

                        {!contextReady && (
                          <div className="alert alert-light border d-flex align-items-center gap-2 mb-4">
                            <i className="ri-information-line text-primary fs-5" />
                            <span className="text-muted small">
                              Select a session, course and subject above to view
                              or add syllabus topics.
                            </span>
                          </div>
                        )}

                        {contextReady && (
                          <>
                            <hr />

                            {/* Step 2 */}
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <span
                                  className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: 24,
                                    height: 24,
                                    fontSize: 12,
                                  }}
                                >
                                  2
                                </span>
                                <h6 className="mb-0">Existing Syllabus</h6>
                              </div>

                              <div style={{ minWidth: 220 }}>
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text bg-transparent">
                                    <i className="ri-search-line" />
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search existing topics"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>

                            {existingTopics.length === 0 ? (
                              <div className="d-flex flex-column align-items-center justify-content-center text-center border rounded-3 py-4 mb-4 bg-light bg-opacity-50">
                                <i className="ri-file-list-3-line fs-2 text-muted mb-2" />
                                <span className="text-muted small">
                                  {search
                                    ? `No topics match "${search}".`
                                    : "No syllabus added yet for this subject."}
                                </span>
                              </div>
                            ) : (
                              <div className="table-responsive mb-4">
                                <table className="table table-bordered align-middle">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="60">#</th>
                                      <th>Topic</th>
                                      <th>Description</th>
                                      <th width="150">Duration</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {existingTopics.map((row, index) => (
                                      <tr key={row.id || index}>
                                        <td className="text-muted">
                                          {index + 1}
                                        </td>
                                        <td>
                                          <strong>{row.title}</strong>
                                        </td>
                                        <td className="text-muted">
                                          {row.description || "-"}
                                        </td>
                                        <td>
                                          <span className="badge bg-label-info">
                                            <i className="ri-time-line me-1" />
                                            {row.duration_day} Days
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            <hr />

                            {/* Step 3: Add New Topics */}
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <span
                                  className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: 24,
                                    height: 24,
                                    fontSize: 12,
                                  }}
                                >
                                  3
                                </span>
                                <div>
                                  <h6 className="mb-0">Add New Syllabus</h6>
                                  <small className="text-muted">
                                    Add topics one by one, or import them from
                                    an Excel file
                                  </small>
                                </div>
                              </div>

                              {/* Manual / Import tabs */}
                              <div
                                className="btn-group btn-group-sm"
                                role="group"
                              >
                                <button
                                  type="button"
                                  className={`btn ${
                                    addMode === "manual"
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  onClick={() => setAddMode("manual")}
                                >
                                  <i className="ri-edit-line me-1" />
                                  Manual Entry
                                </button>
                                <button
                                  type="button"
                                  className={`btn ${
                                    addMode === "import"
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  onClick={() => setAddMode("import")}
                                >
                                  <i className="ri-file-excel-2-line me-1" />
                                  Import from Excel
                                </button>
                              </div>
                            </div>

                            {/* -------------------------------- */}
                            {/* Manual entry (original behaviour) */}
                            {/* -------------------------------- */}
                            {addMode === "manual" && (
                              <>
                                <div className="d-flex justify-content-end mb-2">
                                  <button
                                    type="button"
                                    className="btn btn-outline-success btn-sm"
                                    onClick={handleAddNewTopic}
                                  >
                                    <i className="ri-add-line me-1" />
                                    Add Row
                                  </button>
                                </div>

                                <form onSubmit={addSyllabus}>
                                  {newTopics.map((row, index) => (
                                    <div
                                      key={index}
                                      className="row g-2 align-items-center mb-2 p-2 rounded-3"
                                      style={{
                                        background:
                                          index % 2 === 0
                                            ? "transparent"
                                            : "rgba(0,0,0,0.02)",
                                      }}
                                    >
                                      <div
                                        className="col-auto d-none d-md-flex align-items-center justify-content-center text-muted small fw-semibold"
                                        style={{ width: 24 }}
                                      >
                                        {index + 1}
                                      </div>

                                      {/* Topic */}
                                      <div className="col-md-3">
                                        <div className="form-floating form-floating-outline">
                                          <input
                                            type="text"
                                            className="form-control input-capitalize"
                                            name="title[]"
                                            value={row.title}
                                            placeholder="Topic"
                                            onChange={(e) =>
                                              handleNewTopicChange(
                                                index,
                                                "title",
                                                e.target.value,
                                              )
                                            }
                                            required
                                          />
                                          <label>Topic</label>
                                        </div>
                                      </div>

                                      {/* Description */}
                                      <div className="col-md-5">
                                        <div className="form-floating form-floating-outline">
                                          <input
                                            type="text"
                                            className="form-control"
                                            name="description[]"
                                            value={row.description}
                                            placeholder="Description"
                                            onChange={(e) =>
                                              handleNewTopicChange(
                                                index,
                                                "description",
                                                e.target.value,
                                              )
                                            }
                                          />
                                          <label>Description</label>
                                        </div>
                                      </div>

                                      {/* Duration */}
                                      <div className="col-md-2">
                                        <div className="form-floating form-floating-outline">
                                          <input
                                            type="number"
                                            className="form-control"
                                            name="duration_day[]"
                                            value={row.duration_day}
                                            placeholder="Duration"
                                            min="1"
                                            onChange={(e) =>
                                              handleNewTopicChange(
                                                index,
                                                "duration_day",
                                                e.target.value,
                                              )
                                            }
                                            required
                                          />
                                          <label>Duration (Days)</label>
                                        </div>
                                      </div>

                                      {/* Buttons */}
                                      <div className="col-md-1 d-flex justify-content-md-center">
                                        {newTopics.length > 1 && (
                                          <button
                                            type="button"
                                            className="btn btn-icon btn-outline-danger"
                                            onClick={() =>
                                              handleDeleteNewTopic(index)
                                            }
                                            title="Remove Topic"
                                          >
                                            <i className="ri-delete-bin-line" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}

                                  {/* Footer: running total + submit */}
                                  <div className="d-flex flex-wrap justify-content-between align-items-center mt-4 pt-3 border-top">
                                    <span className="text-muted small">
                                      {newTopicsDuration > 0 ? (
                                        <>
                                          New rows add up to{" "}
                                          <strong>{newTopicsDuration}</strong>{" "}
                                          days
                                        </>
                                      ) : (
                                        "Fill in the rows above to add syllabus topics"
                                      )}
                                    </span>

                                    <button
                                      type="submit"
                                      className="btn btn-primary px-5"
                                      disabled={loading || !contextReady}
                                    >
                                      {loading ? (
                                        <>
                                          <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                          />
                                          Submitting...
                                        </>
                                      ) : (
                                        <>
                                          <i className="ri-save-line me-1" />
                                          Save Syllabus
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </form>
                              </>
                            )}

                            {/* -------------------------------- */}
                            {/* Excel import */}
                            {/* -------------------------------- */}
                            {addMode === "import" && (
                              <div>
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    className="form-control"
                                    style={{ maxWidth: 320 }}
                                    onChange={handleImportFile}
                                    disabled={importing}
                                  />

                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={downloadImportTemplate}
                                  >
                                    <i className="ri-download-2-line me-1" />
                                    Download Template
                                  </button>

                                  {importRows.length > 0 && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={resetImport}
                                      disabled={importing}
                                    >
                                      <i className="ri-close-line me-1" />
                                      Clear
                                    </button>
                                  )}
                                </div>

                                <div className="alert alert-light border py-2 px-3 mb-3">
                                  <small className="text-muted">
                                    Expected columns: <strong>Topic</strong>,{" "}
                                    <strong>Description</strong> (optional),{" "}
                                    <strong>Duration</strong> (in days). Use the
                                    template button above to get the correct
                                    headers.
                                  </small>
                                </div>

                                {importRows.length > 0 && (
                                  <>
                                    {/* Stats bar */}
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                      <span className="badge bg-label-secondary rounded-pill px-3 py-2">
                                        Total: {importTotal}
                                      </span>
                                      <span className="badge bg-label-success rounded-pill px-3 py-2">
                                        <i className="ri-check-line me-1" />
                                        Uploaded: {importSuccessCount}
                                      </span>
                                      <span className="badge bg-label-danger rounded-pill px-3 py-2">
                                        <i className="ri-close-line me-1" />
                                        Failed: {importFailedCount}
                                      </span>
                                      <span className="badge bg-label-warning rounded-pill px-3 py-2">
                                        <i className="ri-time-line me-1" />
                                        Remaining: {importRemainingCount}
                                      </span>
                                    </div>

                                    <div
                                      className="progress mb-3"
                                      style={{ height: 6 }}
                                    >
                                      <div
                                        className="progress-bar bg-success"
                                        style={{
                                          width: `${importDonePct}%`,
                                        }}
                                      />
                                    </div>

                                    <div
                                      className="table-responsive mb-3"
                                      style={{
                                        maxHeight: 360,
                                        overflowY: "auto",
                                      }}
                                    >
                                      <table className="table table-bordered align-middle mb-0">
                                        <thead className="table-light">
                                          <tr>
                                            <th width="50">#</th>
                                            <th>Topic</th>
                                            <th>Description</th>
                                            <th width="110">Duration</th>
                                            <th width="130">Status</th>
                                            <th width="50" />
                                          </tr>
                                        </thead>

                                        <tbody>
                                          {importRows.map((row, index) => (
                                            <tr key={row.rowId}>
                                              <td className="text-muted">
                                                {index + 1}
                                              </td>
                                              <td>{row.title || "-"}</td>
                                              <td className="text-muted">
                                                {row.description || "-"}
                                              </td>
                                              <td>{row.duration_day || "-"}</td>
                                              <td>
                                                {row.status === "pending" && (
                                                  <span className="badge bg-label-secondary">
                                                    Pending
                                                  </span>
                                                )}
                                                {row.status === "uploading" && (
                                                  <span className="badge bg-label-info">
                                                    <span
                                                      className="spinner-border spinner-border-sm me-1"
                                                      style={{
                                                        width: 10,
                                                        height: 10,
                                                      }}
                                                    />
                                                    Uploading
                                                  </span>
                                                )}
                                                {row.status === "success" && (
                                                  <span className="badge bg-label-success">
                                                    <i className="ri-check-line me-1" />
                                                    Uploaded
                                                  </span>
                                                )}
                                                {row.status === "failed" && (
                                                  <span
                                                    className="badge bg-label-danger"
                                                    title={row.error}
                                                  >
                                                    <i className="ri-error-warning-line me-1" />
                                                    Failed
                                                  </span>
                                                )}
                                                {row.status === "failed" &&
                                                  row.error && (
                                                    <div
                                                      className="text-danger mt-1"
                                                      style={{
                                                        fontSize: 11,
                                                      }}
                                                    >
                                                      {row.error}
                                                    </div>
                                                  )}
                                              </td>
                                              <td>
                                                {row.status !== "uploading" &&
                                                  row.status !== "success" && (
                                                    <button
                                                      type="button"
                                                      className="btn btn-icon btn-outline-danger btn-sm"
                                                      title="Remove Row"
                                                      onClick={() =>
                                                        removeImportRow(
                                                          row.rowId,
                                                        )
                                                      }
                                                      disabled={importing}
                                                    >
                                                      <i className="ri-delete-bin-line" />
                                                    </button>
                                                  )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>

                                    <div className="d-flex flex-wrap justify-content-between align-items-center pt-3 border-top">
                                      <span className="text-muted small">
                                        {importFileName && (
                                          <>
                                            <i className="ri-file-excel-2-line me-1" />
                                            {importFileName}
                                          </>
                                        )}
                                      </span>

                                      <button
                                        type="button"
                                        className="btn btn-primary px-5"
                                        onClick={startImport}
                                        disabled={
                                          importing ||
                                          importRemainingCount === 0
                                        }
                                      >
                                        {importing ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm me-2"
                                              role="status"
                                            />
                                            Importing...
                                          </>
                                        ) : importFailedCount > 0 &&
                                          importSuccessCount > 0 ? (
                                          <>
                                            <i className="ri-refresh-line me-1" />
                                            Retry Failed ({importFailedCount})
                                          </>
                                        ) : (
                                          <>
                                            <i className="ri-upload-2-line me-1" />
                                            Start Import
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
